// Credentials that travel through the app in places telemetry picks up on its
// own: the password-reset token sits in the /reset-password/:token path (and so
// in every URL, page view, transaction name and breadcrumb taken on that page),
// and passwords and login codes sit in GraphQL request bodies. Each telemetry
// sink runs its outgoing data through these before it leaves the browser.

export const REDACTED = '[redacted]';

// Case-insensitive like the router, which serves /RESET-PASSWORD/:token too.
const RESET_TOKEN_PATH = /(\/reset-password\/)[^/?#\s"']+/gi;

export const redactSensitiveUrls = (value: string): string =>
  value.replace(RESET_TOKEN_PATH, `$1${REDACTED}`);

/**
 * Redacts every string inside `value`, in place, and returns it. Telemetry
 * events are plain JSON-ish trees whose URL-bearing fields differ per SDK and
 * per version, so walking the whole event is what keeps a newly added field
 * from leaking the token.
 */
export const redactSensitiveUrlsDeep = <T>(value: T): T => {
  const seen = new WeakSet<object>();

  const walk = (node: unknown): unknown => {
    if (typeof node === 'string') {
      return redactSensitiveUrls(node);
    }
    if (!node || typeof node !== 'object' || seen.has(node)) {
      return node;
    }
    seen.add(node);

    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        node[index] = walk(item);
      });
    } else {
      const record = node as Record<string, unknown>;
      Object.keys(record).forEach((key) => {
        record[key] = walk(record[key]);
      });
    }
    return node;
  };

  return walk(value) as T;
};

// Matched against variable names anywhere in a GraphQL request, so a new auth
// operation is covered without being listed. A false positive only blanks a
// value in a recording.
const SENSITIVE_VARIABLE = /password|token|secret|^code$/i;

const redactVariables = (node: unknown): unknown => {
  if (Array.isArray(node)) {
    return node.map(redactVariables);
  }
  if (!node || typeof node !== 'object') {
    return node;
  }
  return Object.fromEntries(
    Object.entries(node).map(([key, value]) => [
      key,
      SENSITIVE_VARIABLE.test(key) ? REDACTED : redactVariables(value),
    ])
  );
};

/**
 * Redacts credential-looking variables from a serialized GraphQL request body
 * (a single operation or a batch). Anything that isn't JSON (a multipart
 * upload) is returned unchanged.
 */
export const redactGraphQLRequestBody = (body: string): string => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return body;
  }

  const redactOperation = (operation: unknown) =>
    operation && typeof operation === 'object' && 'variables' in operation
      ? {
          ...operation,
          variables: redactVariables(
            (operation as { variables: unknown }).variables
          ),
        }
      : operation;

  return JSON.stringify(
    Array.isArray(parsed)
      ? parsed.map(redactOperation)
      : redactOperation(parsed)
  );
};

// Query keys that carry a credential as one of their parts.
const SENSITIVE_QUERY_KEYS = new Set(['verify-reset-token']);

/**
 * A query key safe to attach to error reports: keys that embed a credential
 * keep their name, so the report still says which query failed, and lose the
 * rest.
 */
export const toReportableQueryKey = (
  queryKey: readonly unknown[] | undefined
): readonly unknown[] | undefined =>
  queryKey && SENSITIVE_QUERY_KEYS.has(String(queryKey[0]))
    ? [queryKey[0], REDACTED]
    : queryKey;
